import { type ToolPostPayload, ToolType } from "@repo/app-types/entities/tool";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { ModalHeader, modalHeader } from "@repo/ui/Modal/modalHeader";
import {
  IconCircleCheck,
  IconPhone,
  IconPhoneEnd,
  IconTools,
} from "@tabler/icons-react";
import { type FC, useState } from "react";
import {
  Dialog,
  GridList,
  GridListItem,
  Header,
  Heading,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useCreateToolMutation } from "../../api/entities/tool.js";
import { appPaths } from "../../app/appPaths.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { CreateToolModalDialog } from "./createToolModalDialog.js";

export interface CreateToolModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ToolTypeOption = {
  type: ToolType;
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
};

const toolTypeOptions: ToolTypeOption[] = [
  {
    type: ToolType.CALL_TRANSFER,
    title: "Call transfer",
    description: "Transfer the call to a human agent or phone number",
    Icon: IconPhone,
    iconColor: "bg-blue-500",
  },
  {
    type: ToolType.CALL_END,
    title: "End call",
    description: "Hang up or return the call to your contact center",
    Icon: IconPhoneEnd,
    iconColor: "bg-red-500",
  },
  {
    type: ToolType.CUSTOM,
    title: "Custom",
    description: "Make API calls to any external service",
    Icon: IconTools,
    iconColor: "bg-purple-500",
  },
];

export const CreateToolModal: FC<CreateToolModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState<ToolType | null>(null);

  const [createTool] = useCreateToolMutation();

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  const handleTypeSelect = (type: ToolType) => {
    setSelectedType(type);
  };

  const handleCreate = async (payload: ToolPostPayload) => {
    try {
      const created = await createTool(payload).unwrap();

      showNotification(dispatch, {
        id: `tool-created-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tool created",
        secondaryMessage: `${created.name} has been created successfully.`,
      });

      handleClose();
      navigate(appPaths.agentTool.build({ toolId: created.id }));
    } catch {
      showNotification(dispatch, {
        id: `tool-create-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to create tool",
        secondaryMessage: "Please try again.",
      });
    }
  };

  // Show type picker
  if (!selectedType) {
    return (
      <CenteredModal
        isOpen={isOpen}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        isDismissable
        size={Size.SMALL}
      >
        <Dialog>
          {() => (
            <>
              <Header
                className={modalHeader().header({
                  className: "px-4 pt-4 sm:px-6 sm:pt-6",
                })}
              >
                <Heading slot="title" className={modalHeader().title()}>
                  Create tool
                </Heading>
                <ModalCloseButton />
              </Header>

              <div className="flex flex-col p-4 sm:p-6">
                <GridList
                  className="flex flex-col gap-3"
                  aria-label="Tool type"
                  layout="stack"
                >
                  {toolTypeOptions.map((option) => {
                    const { Icon, iconColor, title, description } = option;

                    return (
                      <GridListItem
                        key={option.type}
                        textValue={option.type}
                        className="group relative flex cursor-pointer items-center gap-4 rounded-lg border border-border-strong bg-white p-4 text-foreground shadow-xs hover:bg-gray-50 focus:outline-hidden focus-visible:border-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-600"
                        onAction={() => {
                          handleTypeSelect(option.type);
                        }}
                      >
                        <div
                          className={twMerge(
                            "flex flex-none items-center justify-center rounded-lg p-2",
                            iconColor,
                          )}
                        >
                          <Icon className="size-6 shrink-0 text-white" />
                        </div>

                        <div className="flex flex-1 flex-col items-start gap-y-1">
                          <span className="text-sm font-semibold">{title}</span>
                          <span className="text-xs text-muted-foreground">
                            {description}
                          </span>
                        </div>

                        <IconCircleCheck className="invisible size-5 flex-none text-indigo-600 group-data-[selected]:visible" />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-focus-visible:border group-data-[selected]:border-indigo-600"
                        />
                      </GridListItem>
                    );
                  })}
                </GridList>
              </div>
            </>
          )}
        </Dialog>
      </CenteredModal>
    );
  }

  return (
    <Modal isOpen={isOpen} onCancel={handleClose} size={Size.LARGE}>
      <ModalHeader onClose={handleClose}>
        Create {selectedType.replace(/_/g, " ")} tool
      </ModalHeader>
      <CreateToolModalDialog type={selectedType} onSave={handleCreate} />
    </Modal>
  );
};
